/**
 * Ethereum Provider Guard
 * Prevents conflicts between multiple wallet extensions trying to set window.ethereum
 */

interface EthereumProvider {
  isMetaMask?: boolean;
  isPhantom?: boolean;
  isCoinbaseWallet?: boolean;
  isRabby?: boolean;
  isBraveWallet?: boolean;
  isOKXWallet?: boolean;
  request?: (args: { method: string; params?: any[] }) => Promise<any>;
  on?: (event: string, handler: (...args: any[]) => void) => void;
  removeListener?: (event: string, handler: (...args: any[]) => void) => void;
  selectedAddress?: string;
  chainId?: string;
  _metamask?: any;
}

declare global {
  interface Window {
    __ETHEREUM_PROVIDER_GUARD_INITIALIZED__?: boolean;
    __PROVIDER_CONFLICT_DETECTED__?: boolean;
  }
}

class EthereumProviderGuard {
  private originalEthereum: EthereumProvider | undefined;
  private providerQueue: EthereumProvider[] = [];
  private isInitialized = false;
  private phantomBlockTimer: NodeJS.Timeout | null = null;
  private lastRequestTimestamp = 0;
  private userInitiatedConnection = false;

  constructor() {
    this.init();
  }

  private init() {
    if (
      typeof window === "undefined" ||
      this.isInitialized ||
      window.__ETHEREUM_PROVIDER_GUARD_INITIALIZED__
    ) {
      return;
    }

    console.log("Initializing Ethereum Provider Guard...");

    // Detect multiple wallet extensions early
    this.detectWalletConflicts();

    // Store the original ethereum provider if it exists
    this.originalEthereum = window.ethereum as any;

    // Prevent Phantom from auto-connecting
    this.preventPhantomAutoConnect();

    // Create a proxy to handle multiple providers
    this.setupProviderProxy();

    // Mark as initialized
    this.isInitialized = true;
    window.__ETHEREUM_PROVIDER_GUARD_INITIALIZED__ = true;

    // Do not patch global Object.defineProperty here. Wallet SDKs rely on
    // provider injection order, and global interception can break unrelated code.
  }

  private detectWalletConflicts() {
    const detectedWallets: string[] = [];

    const ethereum = (window as any).ethereum;
    if (ethereum?.isMetaMask) detectedWallets.push("MetaMask");
    if (ethereum?.isPhantom) detectedWallets.push("Phantom");
    if (ethereum?.isCoinbaseWallet) detectedWallets.push("Coinbase");
    if (ethereum?.isRabby) detectedWallets.push("Rabby");
    if (ethereum?.isBraveWallet) detectedWallets.push("Brave");
    if (ethereum?.isOKXWallet) detectedWallets.push("OKX (Ethereum)");

    // Check for OKX wallet specifically
    if ((window as any).okxwallet) detectedWallets.push("OKX");
    if ((window as any).okex) detectedWallets.push("OKX (Legacy)");

    // Check for Phantom Solana
    if ((window as any).phantom) detectedWallets.push("Phantom (Solana)");

    console.log("Detected wallet extensions:", detectedWallets);

    if (detectedWallets.length > 1) {
      console.warn("Multiple wallet extensions detected:", detectedWallets);
      window.__PROVIDER_CONFLICT_DETECTED__ = true;

      // Emit custom event for UI to handle
      window.dispatchEvent(
        new CustomEvent("wallet-conflict-detected", {
          detail: { wallets: detectedWallets },
        })
      );
    }
  }

  private preventPhantomAutoConnect() {
    // Prevent Phantom's auto-connect behavior
    const phantom = (window as any).phantom;
    if (phantom?.ethereum) {
      // Temporarily disable phantom's auto-connect
      const originalPhantom = phantom.ethereum;

      // Override phantom's request method to prevent auto-popups
      if (originalPhantom.request) {
        const originalRequest = originalPhantom.request;
        originalPhantom.request = async (args: any) => {
          // Block unsolicited connection attempts
          if (
            (args.method === "eth_requestAccounts" ||
              args.method === "wallet_requestPermissions") &&
            !this.userInitiatedConnection
          ) {
            console.log(
              "EthereumProviderGuard: Blocking unsolicited Phantom connection attempt"
            );
            throw new Error(
              "Connection request blocked - user must initiate connection"
            );
          }

          return originalRequest.call(originalPhantom, args);
        };
      }
    }
  }

  public setUserInitiatedConnection(initiated: boolean) {
    this.userInitiatedConnection = initiated;
    this.lastRequestTimestamp = Date.now();

    // Clear the flag after 30 seconds
    if (initiated) {
      setTimeout(() => {
        this.userInitiatedConnection = false;
      }, 30000);
    }
  }

  private setupProviderProxy() {
    if (!(window as any).ethereum) return;

    const originalProvider = (window as any).ethereum;

    // Store the original request method to avoid proxy recursion
    const originalRequest = originalProvider.request?.bind(originalProvider);

    // Create a proxy that handles multiple providers gracefully
    const providerProxy = new Proxy(originalProvider, {
      set: (target: any, property: string | symbol, value: any) => {
        // Prevent overwriting critical properties
        if (
          property === "ethereum" ||
          property === "isMetaMask" ||
          property === "isPhantom"
        ) {
          console.warn(
            `EthereumProviderGuard: Prevented overwrite of ${String(property)}`
          );
          return true;
        }

        target[property] = value;
        return true;
      },

      get: (target: any, property: string | symbol) => {
        // Handle method calls that might conflict
        if (property === "request" && typeof originalRequest === "function") {
          return async (args: { method: string; params?: any[] }) => {
            // For wallet connection requests, emit events for our connection manager to handle
            if (
              args.method === "eth_requestAccounts" ||
              args.method === "wallet_requestPermissions"
            ) {
              console.log(
                "EthereumProviderGuard: Handling wallet connection request from:",
                target.isMetaMask
                  ? "MetaMask"
                  : target.isPhantom
                  ? "Phantom"
                  : target.isRabby
                  ? "Rabby"
                  : target.isCoinbaseWallet
                  ? "Coinbase"
                  : "Unknown"
              );

              // Check if this is an unsolicited request (e.g., Phantom auto-popup)
              const timeSinceLastUserAction =
                Date.now() - this.lastRequestTimestamp;
              const isUnsolicitedRequest =
                !this.userInitiatedConnection && timeSinceLastUserAction > 5000;

              if (isUnsolicitedRequest && target.isPhantom) {
                console.log(
                  "EthereumProviderGuard: Blocking unsolicited Phantom connection attempt"
                );
                throw new Error(
                  "Connection request blocked - user must initiate connection through the Connect button"
                );
              }

              try {
                const result = await originalRequest(args);
                return result;
              } catch (error: any) {
                console.warn(
                  "EthereumProviderGuard: Connection request failed:",
                  error
                );

                // Emit custom events for specific wallets
                if (target.isPhantom) {
                  window.dispatchEvent(
                    new CustomEvent("phantom-error", {
                      detail: { error, method: args.method },
                    })
                  );
                } else if (target.isMetaMask) {
                  window.dispatchEvent(
                    new CustomEvent("metamask-error", {
                      detail: { error, method: args.method },
                    })
                  );
                } else {
                  // Generic wallet error
                  window.dispatchEvent(
                    new CustomEvent("wallet-connection-error", {
                      detail: { error, method: args.method },
                    })
                  );
                }

                throw error;
              }
            }

            try {
              return await originalRequest(args);
            } catch (error: any) {
              console.warn(
                "EthereumProviderGuard: Provider request failed, trying fallback:",
                error
              );

              // Only try fallbacks for non-connection requests to avoid multiple connection prompts
              if (
                args.method !== "eth_requestAccounts" &&
                args.method !== "wallet_requestPermissions"
              ) {
                // Try other providers in queue
                for (const provider of this.providerQueue) {
                  if (provider.request && provider !== target) {
                    try {
                      return await provider.request(args);
                    } catch (fallbackError) {
                      console.warn(
                        "EthereumProviderGuard: Fallback provider also failed:",
                        fallbackError
                      );
                    }
                  }
                }
              }

              throw error;
            }
          };
        }

        return target[property];
      },
    });

    // Replace window.ethereum with our proxy - use safer approach
    try {
      // First, try to check if ethereum property is configurable
      const descriptor = Object.getOwnPropertyDescriptor(window, "ethereum");

      if (!descriptor || descriptor.configurable !== false) {
        Object.defineProperty(window, "ethereum", {
          value: providerProxy,
          writable: true,
          configurable: true,
        });
        console.log(
          "EthereumProviderGuard: Successfully set up provider proxy"
        );
      } else {
        console.warn(
          "EthereumProviderGuard: Ethereum property is not configurable, using direct assignment"
        );
        // Direct assignment as fallback
        try {
          (window as any).ethereum = providerProxy;
        } catch (assignError) {
          console.error(
            "EthereumProviderGuard: Failed to assign proxy:",
            assignError
          );
          // If we can't replace it, just continue without the proxy
        }
      }
    } catch (error) {
      console.warn(
        "EthereumProviderGuard: Could not set up provider proxy:",
        error
      );
      // Try direct assignment as last resort
      try {
        (window as any).ethereum = providerProxy;
      } catch (assignError) {
        console.error(
          "EthereumProviderGuard: Final fallback assignment failed:",
          assignError
        );
      }
    }
  }

  public addProvider(provider: EthereumProvider) {
    if (!provider || this.providerQueue.includes(provider)) {
      return;
    }

    console.log("EthereumProviderGuard: Adding provider to queue:", {
      isMetaMask: provider.isMetaMask,
      isPhantom: provider.isPhantom,
      isCoinbaseWallet: provider.isCoinbaseWallet,
      isRabby: provider.isRabby,
      isBraveWallet: provider.isBraveWallet,
      isOKXWallet: provider.isOKXWallet,
    });

    this.providerQueue.push(provider);
  }

  public getProviders(): EthereumProvider[] {
    return [...this.providerQueue];
  }

  public getPrimaryProvider(): any {
    return (window as any).ethereum || this.originalEthereum;
  }

  public reset() {
    this.providerQueue = [];
    this.isInitialized = false;
    if (typeof window !== "undefined") {
      window.__ETHEREUM_PROVIDER_GUARD_INITIALIZED__ = false;
    }
  }
}

// Create singleton instance
const ethereumProviderGuard = new EthereumProviderGuard();

export default ethereumProviderGuard;

// Export utility functions
export const getEthereumProvider = () =>
  ethereumProviderGuard.getPrimaryProvider();
export const getAllEthereumProviders = () =>
  ethereumProviderGuard.getProviders();
export const addEthereumProvider = (provider: any) =>
  ethereumProviderGuard.addProvider(provider);
